<script setup>
import CaptainPaywall from 'dashboard/components-next/captain/pageComponents/Paywall.vue';
import SettingsHeader from 'dashboard/components-next/captain/pageComponents/settings/SettingsHeader.vue';
import CustomToolsPageEmptyState from 'dashboard/components-next/captain/pageComponents/emptyStates/CustomToolsPageEmptyState.vue';
import CustomToolCard from 'dashboard/components-next/captain/pageComponents/customTool/CustomToolCard.vue';
import Button from 'dashboard/components-next/button/Button.vue';

defineProps({
  showPaywall: { type: Boolean, default: false },
  hasEntitlement: { type: Boolean, default: false },
  loadError: { type: String, default: '' },
  isFetching: { type: Boolean, default: false },
  showSoftLimitWarning: { type: Boolean, default: false },
  tools: { type: Array, default: () => [] },
  canManage: { type: Boolean, default: false },
  savingToolId: { type: [Number, String], default: null },
  currentPage: { type: Number, default: 1 },
});

const emit = defineEmits(['retry', 'create', 'action']);
</script>

<template>
  <section class="flex flex-col gap-4">
    <SettingsHeader
      :heading="$t('CAPTAIN.CUSTOM_TOOLS.MANAGEMENT.CUSTOM_TITLE')"
      :description="$t('CAPTAIN.CUSTOM_TOOLS.FORM_DESCRIPTION')"
    />
    <CaptainPaywall v-if="showPaywall" feature-prefix="CAPTAIN.CUSTOM_TOOLS" />
    <p
      v-else-if="!hasEntitlement"
      class="rounded-xl border border-dashed border-n-weak p-5 text-sm text-n-slate-11"
    >
      {{ $t('CAPTAIN.CUSTOM_TOOLS.MANAGEMENT.ENTITLEMENT_REQUIRED') }}
    </p>
    <template v-else>
      <div
        v-if="loadError"
        role="alert"
        :aria-busy="isFetching"
        class="flex flex-col gap-4 rounded-xl border border-n-ruby-7 bg-n-ruby-2 p-4 sm:flex-row sm:items-center sm:justify-between"
      >
        <div class="flex min-w-0 items-start gap-3">
          <span
            class="i-lucide-circle-alert mt-0.5 size-5 shrink-0 text-n-ruby-10"
            aria-hidden="true"
          />
          <div class="min-w-0">
            <p class="text-sm font-semibold text-n-ruby-11">
              {{
                $t('CAPTAIN.CUSTOM_TOOLS.MANAGEMENT.CUSTOM_LOAD_ERROR_TITLE')
              }}
            </p>
            <p class="mt-1 break-words text-sm text-n-ruby-11">
              {{ loadError }}
            </p>
          </div>
        </div>
        <Button
          data-testid="custom-tools-retry"
          type="button"
          color="slate"
          variant="faded"
          icon="i-lucide-refresh-cw"
          :label="$t('CAPTAIN.CUSTOM_TOOLS.MANAGEMENT.RETRY_CUSTOM_TOOLS')"
          :is-loading="isFetching"
          class="w-full shrink-0 sm:w-auto"
          @click="emit('retry', currentPage || 1)"
        />
      </div>
      <template v-else>
        <div
          v-if="showSoftLimitWarning"
          class="flex items-center gap-2 rounded-lg bg-n-amber-2 px-4 py-3 text-sm text-n-amber-11"
        >
          <span
            class="i-lucide-triangle-alert size-4 shrink-0"
            aria-hidden="true"
          />
          {{ $t('CAPTAIN.CUSTOM_TOOLS.SOFT_LIMIT_WARNING') }}
        </div>
        <CustomToolCard
          v-for="tool in tools"
          :id="tool.id"
          :key="tool.id"
          :title="tool.title"
          :description="tool.description"
          :endpoint-url="tool.endpoint_url"
          :http-method="tool.http_method"
          :auth-type="tool.auth_type"
          :risk-level="tool.risk_level"
          :has-auth-secret="tool.has_auth_secret"
          :param-schema="tool.param_schema"
          :enabled="tool.enabled"
          :created-at="tool.created_at"
          :updated-at="tool.updated_at"
          :can-manage="canManage"
          :is-updating-availability="savingToolId === tool.id"
          @action="emit('action', $event)"
        />
        <CustomToolsPageEmptyState
          v-if="!tools.length && canManage"
          @click="emit('create')"
        />
        <p
          v-else-if="!tools.length"
          class="rounded-xl border border-dashed border-n-weak p-5 text-sm text-n-slate-11"
        >
          {{ $t('CAPTAIN.CUSTOM_TOOLS.MANAGEMENT.NO_CUSTOM_TOOLS') }}
        </p>
      </template>
    </template>
  </section>
</template>
